/**
 * Stage set renderer (DEV placeholder art, procedural and deterministic). Draws the ground from the
 * stage sketch + palette, adds per-stage dressing (mast, bed, lamp, loom, well, mountain ridges,
 * pavilion, sun) and animates the cheap bits (water dashes, rain, snow, dust, lamp flicker, mist).
 * Real environment art replaces this file's drawing by texture key; the sketches stay the collision truth.
 */
import Phaser from 'phaser';
import { Depth, GAME_H, GAME_W } from '@/core/config';
import type { SeededRng } from '@/core/rng';
import type { StageDef } from './defs';

export interface StageSet {
  /** Fixed-step animation hook (dt in ms). */
  step(dtMs: number): void;
  destroy(): void;
}

const T = 32;
const q = Math.round;

function shade(c: number, f: number): number {
  const r = Math.min(255, Math.floor(((c >> 16) & 255) * f)), g = Math.min(255, Math.floor(((c >> 8) & 255) * f)), b = Math.min(255, Math.floor((c & 255) * f));
  return (r << 16) | (g << 8) | b;
}

class StageSetImpl implements StageSet {
  private readonly objects: Phaser.GameObjects.GameObject[] = [];
  private readonly water: { x: number; y: number }[] = [];
  private waterG: Phaser.GameObjects.Graphics | null = null;
  private weatherG: Phaser.GameObjects.Graphics | null = null;
  private lampG: Phaser.GameObjects.Graphics | null = null;
  private mistG: Phaser.GameObjects.Graphics | null = null;
  private frame = 0;
  private tick = 0;
  private timeMs = 0;
  private particles: { x: number; y: number; vx: number; vy: number; len: number }[] = [];
  private mist: { x: number; y: number; w: number; h: number; v: number }[] = [];

  constructor(private readonly scene: Phaser.Scene, private readonly def: StageDef, private readonly rng: SeededRng) {
    this.drawGround();
    switch (def.id) {
      case 'memory-storm': this.dressStorm(); break;
      case 'memory-island': this.dressIsland(); break;
      case 'memory-black-water': this.dressSickroom(); break;
      case 'memory-farewell': this.dressVillage(); break;
      case 'memory-sword-training': this.dressRoad(); break;
    }
    if (this.water.length) { this.waterG = this.add(scene.add.graphics().setDepth(Depth.GROUND_DETAIL + 1)); this.drawWater(); }
  }

  private add<T extends Phaser.GameObjects.GameObject>(o: T): T { this.objects.push(o); return o; }
  private prop(x: number, y: number, depthY = y): Phaser.GameObjects.Graphics {
    return this.add(this.scene.add.graphics({ x, y }).setDepth(Depth.WORLD_BASE + depthY));
  }

  // ------------------------------------------------------------------ ground
  private drawGround(): void {
    const g = this.add(this.scene.add.graphics().setDepth(Depth.GROUND));
    const rows = this.def.sketch;
    const pal = this.def.palette;
    rows.forEach((row, ty) => {
      for (let tx = 0; tx < row.length; tx++) {
        const ch = row[tx] as string;
        const base = pal[ch] ?? 0xff00ff;
        const x = tx * T, y = ty * T;
        g.fillStyle(base, 1); g.fillRect(x, y, T, T);
        switch (ch) {
          case '.': this.detailFloor(g, x, y, base); break;
          case '~': this.water.push({ x, y }); g.fillStyle(shade(base, 1.18), 0.5); g.fillRect(x + 4, y + 9, 10, 1); g.fillRect(x + 18, y + 22, 9, 1); break;
          case '#': this.detailSolid(g, x, y, base, rows[ty - 1]?.[tx] !== '#'); break;
          case 'M': this.detailMountain(g, x, y, base, ty); break;
          case 'L': this.detailLoom(g, x, y, base); break;
          case 'W': this.detailWell(g, x, y, pal['.'] ?? base); break;
          case 'P': g.fillStyle(shade(base, 0.8), 1); for (let yy = 0; yy < T; yy += 6) g.fillRect(x, y + yy, T, 1); break;
          default: break;
        }
      }
    });
  }

  private detailFloor(g: Phaser.GameObjects.Graphics, x: number, y: number, base: number): void {
    const id = this.def.id;
    const n = id === 'memory-storm' ? 0 : 5;
    g.fillStyle(shade(base, 0.86), 1);
    for (let i = 0; i < n; i++) g.fillRect(x + this.rng.int(1, T - 2), y + this.rng.int(1, T - 2), 1, 1);
    if (id === 'memory-storm') { // deck planks
      g.fillStyle(shade(base, 0.7), 1); for (let yy = 0; yy < T; yy += 8) g.fillRect(x, y + yy, T, 1);
      g.fillStyle(shade(base, 1.15), 1); g.fillRect(x + this.rng.int(2, 26), y + this.rng.int(1, 30), 3, 1);
    } else if (id === 'memory-black-water') { // floorboards
      g.fillStyle(shade(base, 0.75), 1); for (let xx = 0; xx < T; xx += 8) g.fillRect(x + xx, y, 1, T);
    } else if (id === 'memory-farewell') { // snow crust
      g.fillStyle(0xffffff, 0.5); g.fillRect(x + this.rng.int(0, 24), y + this.rng.int(0, 28), this.rng.int(3, 8), 1);
    } else if (id === 'memory-sword-training') { // wheel ruts
      g.fillStyle(shade(base, 0.85), 1); g.fillRect(x, y + 10, T, 1); g.fillRect(x, y + 21, T, 1);
    }
  }

  private detailSolid(g: Phaser.GameObjects.Graphics, x: number, y: number, base: number, topEdge: boolean): void {
    switch (this.def.id) {
      case 'memory-island': // moored hull
        g.fillStyle(shade(base, 0.6), 1); for (let yy = 0; yy < T; yy += 6) g.fillRect(x, y + yy, T, 1);
        if (topEdge) { g.fillStyle(0x8a5a32, 1); g.fillRect(x, y, T, 3); }
        break;
      case 'memory-black-water': // timber wall
        g.fillStyle(shade(base, 0.7), 1); for (let xx = 0; xx < T; xx += 8) g.fillRect(x + xx, y, 1, T);
        if (topEdge) { g.fillStyle(shade(base, 1.6), 1); g.fillRect(x, y, T, 4); }
        break;
      case 'memory-farewell': // house wall with snow on top
        g.fillStyle(shade(base, 0.8), 1); for (let yy = 4; yy < T; yy += 8) g.fillRect(x + ((yy / 8) % 2 ? 8 : 0), y + yy, 12, 6);
        if (topEdge) { g.fillStyle(0xf4f7f9, 1); g.fillRect(x, y, T, 4); }
        break;
      case 'memory-sword-training': // dry bank with grass tufts
        g.fillStyle(shade(base, 0.8), 1); for (let i = 0; i < 4; i++) { const px = x + this.rng.int(2, 28), py = y + this.rng.int(4, 28); g.fillRect(px, py - 3, 1, 3); g.fillRect(px + 2, py - 2, 1, 2); }
        break;
      default: break;
    }
  }

  private detailMountain(g: Phaser.GameObjects.Graphics, x: number, y: number, base: number, ty: number): void {
    // three ridges (三重): each row a lighter, snowier band
    const f = ty <= 1 ? 1.25 : ty === 2 ? 1.08 : 0.92;
    g.fillStyle(shade(base, f), 1); g.fillRect(x, y, T, T);
    g.fillStyle(0xf4f7f9, 0.9); g.fillRect(x + this.rng.int(0, 20), y + 2, this.rng.int(6, 12), 2);
    g.fillStyle(shade(base, 0.7), 1); g.fillRect(x + this.rng.int(4, 26), y + this.rng.int(8, 26), 1, this.rng.int(3, 7));
  }

  private detailLoom(g: Phaser.GameObjects.Graphics, x: number, y: number, base: number): void {
    g.fillStyle(shade(base, 0.6), 1); g.fillRect(x + 4, y + 2, 3, 28); g.fillRect(x + 25, y + 2, 3, 28); g.fillRect(x + 4, y + 2, 24, 3);
    g.fillStyle(0x2b3a5c, 1); for (let i = 0; i < 6; i++) g.fillRect(x + 8 + i * 3, y + 6, 1, 22);
    g.fillStyle(0xe2c48c, 1); g.fillRect(x + 7, y + 16, 18, 1);
  }

  private detailWell(g: Phaser.GameObjects.Graphics, x: number, y: number, floor: number): void {
    g.fillStyle(floor, 1); g.fillRect(x, y, T, T);
    g.fillStyle(0x3a3f57, 1); g.fillEllipse(x + 16, y + 20, 26, 14);
    g.fillStyle(0x8a8f99, 1); g.fillEllipse(x + 16, y + 17, 26, 14);
    g.fillStyle(0x1f2f3a, 1); g.fillEllipse(x + 16, y + 17, 16, 8);
    g.fillStyle(0x5a3a1e, 1); g.fillRect(x + 3, y + 2, 2, 16); g.fillRect(x + 27, y + 2, 2, 16); g.fillRect(x + 3, y + 2, 26, 2);
  }

  private drawWater(): void {
    const g = this.waterG; if (!g) return;
    g.clear();
    const storm = this.def.id === 'memory-storm';
    const ice = this.def.id === 'memory-farewell';
    const base = this.def.palette['~'] ?? 0x1f8f8a;
    const f = this.frame;
    for (const w of this.water) {
      if (ice) { g.fillStyle(0xffffff, 0.35); g.fillRect(w.x + 6, w.y + 12, 14, 1); g.fillStyle(shade(base, 0.85), 1); g.fillRect(w.x + 18, w.y + 20, 8, 1); continue; }
      const k = ((w.x / T + w.y / T) | 0) & 3;
      const ph = (k + f) & 3;
      g.fillStyle(shade(base, ph === 1 ? 1.6 : 1.3), storm ? 0.7 : 0.5);
      g.fillRect(w.x + 3 + ph * 2, w.y + 7, storm ? 12 : 9, 1);
      g.fillRect(w.x + 17 - ph, w.y + 21, storm ? 11 : 8, 1);
      if (storm && ph === 2) { g.fillStyle(0xdde8ff, 0.8); g.fillRect(w.x + 2 + k * 5, w.y + 14, 6, 2); }
    }
  }

  // ------------------------------------------------------------------ dressing
  private dressStorm(): void {
    const mast = this.prop(320, 208);
    mast.fillStyle(0x241810, 1); mast.fillRect(-3, -168, 6, 168);
    mast.fillStyle(0x8a5a32, 1); mast.fillRect(-1, -168, 1, 168);
    mast.fillStyle(0xc9bfa6, 1); mast.fillRect(-26, -150, 52, 8); // furled sail
    mast.fillStyle(0x3a2a1e, 1); mast.fillRect(-30, -152, 60, 2);
    const rails = this.add(this.scene.add.graphics().setDepth(Depth.GROUND_DETAIL));
    rails.fillStyle(0x241810, 1); rails.fillRect(4 * T, 4 * T - 2, 14 * T, 3); rails.fillRect(4 * T, 9 * T - 3, 14 * T, 3);
    rails.fillStyle(0xdde8ff, 0.6); rails.fillRect(3 * T, 9 * T - 1, 15 * T, 1);
    for (let x = 4 * T; x <= 17 * T; x += 48) { rails.fillStyle(0x3a2a1e, 1); rails.fillRect(x, 4 * T - 12, 3, 12); rails.fillRect(x, 9 * T - 14, 3, 12); }
    this.weatherG = this.add(this.scene.add.graphics().setScrollFactor(0).setDepth(Depth.WEATHER));
    this.particles = Array.from({ length: 150 }, () => ({ x: this.rng.range(-20, GAME_W + 20), y: this.rng.range(0, GAME_H), vx: -1.4, vy: this.rng.range(9, 13), len: this.rng.int(6, 12) }));
  }

  private dressIsland(): void {
    const mast = this.prop(192, 96, 100);
    mast.fillStyle(0x241810, 1); mast.fillRect(-2, -76, 4, 76);
    mast.fillStyle(0xc9bfa6, 1); mast.fillRect(-20, -64, 40, 6);
    for (let i = 0; i < 4; i++) {
      const x = this.rng.int(2, 18) * T + 16, y = this.rng.pick([4, 5, 9, 10]) * T + 24;
      const rock = this.prop(x, y);
      rock.fillStyle(0x6b6f78, 1); rock.fillEllipse(0, 0, 14, 8);
      rock.fillStyle(0x8a8f99, 1); rock.fillEllipse(-2, -2, 8, 4);
    }
    const shells = this.add(this.scene.add.graphics().setDepth(Depth.GROUND_DETAIL));
    for (let i = 0; i < 18; i++) { shells.fillStyle(this.rng.chance(0.5) ? 0xf6ecd6 : 0xb7a48a, 1); shells.fillRect(this.rng.int(32, 600), this.rng.int(110, 340), 2, 1); }
    this.mistG = this.add(this.scene.add.graphics().setScrollFactor(0).setDepth(Depth.WEATHER - 2));
    this.mist = Array.from({ length: 5 }, () => ({ x: this.rng.range(-200, GAME_W), y: this.rng.range(40, 300), w: this.rng.range(160, 320), h: this.rng.range(14, 26), v: this.rng.range(3, 7) }));
  }

  private dressSickroom(): void {
    const bed = this.add(this.scene.add.graphics().setDepth(Depth.GROUND_DETAIL));
    bed.fillStyle(0x3a2412, 1); bed.fillRect(12 * T - 6, 4 * T - 8, 2 * T + 12, 2 * T + 12);
    bed.fillStyle(0x8a5a32, 1); bed.fillRect(12 * T - 2, 4 * T - 4, 2 * T + 4, 2 * T + 4);
    bed.fillStyle(0x6a4a5a, 1); bed.fillRect(12 * T, 4 * T + 10, 2 * T, 2 * T - 12);
    bed.fillStyle(0xe2c48c, 1); bed.fillRect(12 * T + 2, 4 * T, 2 * T - 4, 8);
    const stool = this.prop(100, 110);
    stool.fillStyle(0x3a2412, 1); stool.fillRect(-9, -8, 18, 8); stool.fillRect(-8, 0, 2, 6); stool.fillRect(6, 0, 2, 6);
    stool.fillStyle(0x8a5a32, 1); stool.fillRect(-6, -14, 12, 6); stool.fillStyle(0xffb347, 1); stool.fillRect(-1, -20, 2, 6); stool.fillStyle(0xfff1c0, 1); stool.fillRect(-1, -22, 2, 2);
    const bowl = this.prop(360, 150);
    bowl.fillStyle(0x3a2412, 1); bowl.fillRect(-10, -6, 20, 6); bowl.fillStyle(0x5a4636, 1); bowl.fillEllipse(0, -6, 14, 5); bowl.fillStyle(0x2f3a1e, 1); bowl.fillEllipse(0, -6, 9, 3);
    const win = this.add(this.scene.add.graphics().setDepth(Depth.GROUND_DETAIL));
    win.fillStyle(0x0b1630, 1); win.fillRect(8 * T + 4, 1 * T + 4, 2 * T - 8, T - 8);
    win.fillStyle(0x3a2412, 1); win.fillRect(8 * T + 4, 1 * T + 4, 2 * T - 8, 2); win.fillRect(8 * T + 4, 2 * T - 6, 2 * T - 8, 2); win.fillRect(9 * T - 1, 1 * T + 4, 2, T - 8);
    win.fillStyle(0x3a2412, 1); win.fillRect(2 * T, 10 * T, 2 * T, 4); // door sill
    this.lampG = this.add(this.scene.add.graphics().setDepth(Depth.WEATHER - 1).setBlendMode(Phaser.BlendModes.ADD));
    this.weatherG = this.add(this.scene.add.graphics().setDepth(Depth.GROUND_DETAIL + 2));
    this.particles = Array.from({ length: 16 }, () => ({ x: this.rng.range(8 * T + 6, 10 * T - 6), y: this.rng.range(1 * T + 6, 2 * T - 6), vx: 0, vy: this.rng.range(0.15, 0.4), len: 1 }));
  }

  private dressVillage(): void {
    for (const [x, y] of [[48, 196], [604, 156], [80, 318], [560, 300]] as const) {
      const tree = this.prop(x, y);
      tree.fillStyle(0x4a3a30, 1); tree.fillRect(-2, -30, 4, 30);
      tree.fillRect(-10, -26, 8, 2); tree.fillRect(2, -20, 9, 2); tree.fillRect(-8, -14, 6, 2);
      tree.fillStyle(0xf4f7f9, 1); tree.fillRect(-10, -27, 8, 1); tree.fillRect(2, -21, 9, 1);
    }
    const ice = this.add(this.scene.add.graphics().setDepth(Depth.GROUND_DETAIL));
    ice.lineStyle(1, 0xffffff, 0.6);
    for (let i = 0; i < 6; i++) { const x = this.rng.int(10, 300), y = this.rng.int(330, 380); ice.lineBetween(x, y, x + this.rng.int(10, 40), y + this.rng.int(-6, 6)); }
    this.weatherG = this.add(this.scene.add.graphics().setScrollFactor(0).setDepth(Depth.WEATHER));
    this.particles = Array.from({ length: 40 }, () => ({ x: this.rng.range(0, GAME_W), y: this.rng.range(0, GAME_H), vx: this.rng.range(-0.2, 0.3), vy: this.rng.range(0.25, 0.6), len: this.rng.int(1, 2) }));
  }

  private dressRoad(): void {
    const pav = this.prop(0, 0, 5 * T + 8);
    for (const px of [8 * T + 3, 11 * T - 6]) for (const py of [3 * T + 2, 5 * T - 8]) { pav.fillStyle(0x3a2412, 1); pav.fillRect(px, py - 24, 4, 32); }
    pav.fillStyle(0x8c2a22, 1); pav.fillTriangle(8 * T - 12, 3 * T - 8, 11 * T + 12, 3 * T - 8, 9 * T + 16, 2 * T - 6);
    pav.fillStyle(0x5a1a14, 1); pav.fillRect(8 * T - 12, 3 * T - 10, 3 * T + 24, 3);
    const sky = this.add(this.scene.add.graphics().setScrollFactor(0).setDepth(Depth.WEATHER - 3).setBlendMode(Phaser.BlendModes.ADD));
    sky.fillStyle(0xffe08a, 0.10); sky.fillCircle(552, 54, 60);
    sky.fillStyle(0xffe08a, 0.18); sky.fillCircle(552, 54, 34);
    sky.fillStyle(0xffd27a, 0.9); sky.fillCircle(552, 54, 18);
    this.weatherG = this.add(this.scene.add.graphics().setScrollFactor(0).setDepth(Depth.WEATHER));
    this.particles = Array.from({ length: 24 }, () => ({ x: this.rng.range(0, GAME_W), y: this.rng.range(120, 300), vx: this.rng.range(-0.8, -0.3), vy: this.rng.range(-0.1, 0.1), len: 1 }));
  }

  // ------------------------------------------------------------------ animation
  step(dtMs: number): void {
    this.tick++;
    this.timeMs += dtMs;
    const storm = this.def.id === 'memory-storm';
    if (this.waterG && this.tick % (storm ? 5 : 10) === 0) { this.frame = (this.frame + 1) & 3; this.drawWater(); }
    if (this.weatherG) this.stepParticles();
    if (this.lampG && this.tick % 12 === 0) {
      const g = this.lampG; g.clear();
      const f = 0.85 + this.rng.range(0, 0.15);
      g.fillStyle(0xffb347, 0.10 * f); g.fillEllipse(100, 96, 210, 130);
      g.fillStyle(0xffb347, 0.14 * f); g.fillEllipse(100, 96, 120, 74);
      g.fillStyle(0xfff1c0, 0.22 * f); g.fillEllipse(100, 92, 40, 26);
    }
    if (this.mistG && this.tick % 2 === 0) {
      const g = this.mistG; g.clear();
      for (const m of this.mist) { m.x += m.v * (dtMs / 1000) * 2; if (m.x > GAME_W + 40) m.x = -m.w - 40; g.fillStyle(0xe8edf0, 0.10); g.fillRect(q(m.x), q(m.y), q(m.w), q(m.h)); }
    }
  }

  private stepParticles(): void {
    const g = this.weatherG; if (!g) return;
    g.clear();
    switch (this.def.id) {
      case 'memory-storm':
        g.fillStyle(0xd6ecf2, 0.5);
        for (const p of this.particles) { p.x += p.vx; p.y += p.vy; if (p.y > GAME_H + 8) { p.y = -p.len - this.rng.range(0, 30); p.x = this.rng.range(-20, GAME_W + 40); } g.fillRect(q(p.x), q(p.y), 1, p.len); }
        break;
      case 'memory-black-water': // snow past the window
        g.fillStyle(0xf4f7f9, 0.9);
        for (const p of this.particles) { p.y += p.vy; if (p.y > 2 * T - 6) p.y = 1 * T + 6; g.fillRect(q(p.x), q(p.y), 1, 1); }
        break;
      case 'memory-farewell':
        g.fillStyle(0xffffff, 0.85);
        for (const p of this.particles) { p.x += p.vx + Math.sin((this.timeMs + p.y * 10) / 900) * 0.15; p.y += p.vy; if (p.y > GAME_H + 2) { p.y = -2; p.x = this.rng.range(0, GAME_W); } if (p.x < -2) p.x = GAME_W + 2; if (p.x > GAME_W + 2) p.x = -2; g.fillRect(q(p.x), q(p.y), p.len, p.len); }
        break;
      case 'memory-sword-training':
        g.fillStyle(0xfff1c0, 0.55);
        for (const p of this.particles) { p.x += p.vx; p.y += Math.sin((this.timeMs + p.x * 7) / 700) * 0.2; if (p.x < -2) { p.x = GAME_W + 2; p.y = this.rng.range(120, 300); } g.fillRect(q(p.x), q(p.y), 1, 1); }
        break;
      default: break;
    }
  }

  destroy(): void {
    for (const o of this.objects) { try { o.destroy(); } catch { /* gone */ } }
    this.objects.length = 0;
    this.waterG = null; this.weatherG = null; this.lampG = null; this.mistG = null;
  }
}

export function buildStageSet(scene: Phaser.Scene, def: StageDef, rng: SeededRng): StageSet {
  return new StageSetImpl(scene, def, rng);
}
